import { SttAdapter, SttCapabilities, SttModelStatus, SttResult } from './stt_adapter';

export class MoonshineAdapter implements SttAdapter {
  readonly capabilities: SttCapabilities = {
    isLocal: true,
    runtime: 'transformers.js-wasm',
    modelId: 'moonshine-tiny-ar',
  };

  private worker: Worker | null = null;
  private messageIdCounter = 0;
  private pendingResolvers = new Map<number, (res: SttResult) => void>();
  private isDisposed = false;

  private status: SttModelStatus = 'idle';
  private progress = 0;
  private error: string | null = null;
  private initPromise: Promise<void> | null = null;
  private progressListeners = new Set<(phase: string, progress: number) => void>();

  constructor() {
    // Lazy worker creation in init
  }

  getStatus(): { status: SttModelStatus; progress: number; error: string | null } {
    return {
      status: this.status,
      progress: this.progress,
      error: this.error,
    };
  }

  async init(onProgress?: (phase: string, progress: number) => void): Promise<void> {
    if (onProgress) {
      this.progressListeners.add(onProgress);
    }

    if (this.status === 'ready' && this.worker) {
      onProgress?.('ready', 1.0);
      return Promise.resolve();
    }

    if (this.initPromise) {
      if (onProgress) {
        onProgress('loading', this.progress / 100.0);
      }
      return this.initPromise;
    }

    this.status = 'loading';
    this.progress = 0;
    this.error = null;

    this.initPromise = new Promise<void>((resolve, reject) => {
      try {
        if (!this.worker) {
          this.worker = new Worker(new URL('./stt_worker.ts', import.meta.url), {
            type: 'module',
          });
        }

        this.worker.onmessage = (e: MessageEvent) => {
          const { type, id, text, latencyMs, phase, progress, error } = e.data;

          if (type === 'init_done') {
            this.status = 'ready';
            this.progress = 100;
            for (const cb of this.progressListeners) {
              cb('ready', 1.0);
            }
            this.progressListeners.clear();
            resolve();
          } else if (type === 'init_progress') {
            this.progress = Math.round((progress || 0) * 100);
            for (const cb of this.progressListeners) {
              cb(phase, progress);
            }
          } else if (type === 'init_error') {
            this.status = 'error';
            this.error = error || 'STT initialization failed';
            this.initPromise = null;
            for (const cb of this.progressListeners) {
              cb('error', 0);
            }
            this.progressListeners.clear();
            reject(new Error(this.error || 'STT initialization failed'));
          } else if (type === 'transcribe_result') {
            const resolver = this.pendingResolvers.get(id);
            if (resolver) {
              this.pendingResolvers.delete(id);
              resolver({ text, latencyMs });
            }
          } else if (type === 'transcribe_error') {
            const resolver = this.pendingResolvers.get(id);
            if (resolver) {
              this.pendingResolvers.delete(id);
              resolver({ text: '', latencyMs: 0 });
            }
          }
        };

        this.worker.onerror = (err) => {
          this.status = 'error';
          this.error = err.message || 'Worker error';
          this.initPromise = null;
          this.progressListeners.clear();
          reject(new Error(`Worker error: ${err.message}`));
        };

        this.worker.postMessage({ type: 'init' });
      } catch (err: any) {
        this.status = 'error';
        this.error = err?.message || 'Failed to spawn worker';
        this.initPromise = null;
        this.progressListeners.clear();
        reject(err);
      }
    });

    return this.initPromise;
  }

  async transcribe(audio: Float32Array): Promise<SttResult> {
    if (this.isDisposed || !this.worker) {
      return { text: '', latencyMs: 0 };
    }

    const id = ++this.messageIdCounter;

    return new Promise<SttResult>((resolve) => {
      this.pendingResolvers.set(id, resolve);
      // Transfer audio buffer to worker
      this.worker?.postMessage(
        {
          type: 'transcribe',
          id,
          audio,
        },
        [audio.buffer]
      );
    });
  }

  cancel(): void {
    // Clear pending resolvers
    for (const resolver of this.pendingResolvers.values()) {
      resolver({ text: '', latencyMs: 0 });
    }
    this.pendingResolvers.clear();
  }

  async dispose(): Promise<void> {
    this.isDisposed = true;
    this.cancel();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
